"use client";

import { useMessages } from "@/lib/i18n/useMessages";
import { Box, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { SLTeamAvatarGroup } from "../other/SLTeamAvatarGroup";
import { SLTrustpilotRating } from "../other/SLTrustpilotRating";
import { SLBrevoCalendarIframe } from "../third-party/SLBrevoCalendarIframe";
import { SLDialog } from "./SLDialog";

interface SLDialogTerminBuchenProps {
  /** Function to handle dialog close */
  handleClose: () => void;
  /** Whether the dialog is open */
  open: boolean;
}

/**
 * Dialog component for booking appointments with integrated calendar iframe
 * and team information display.
 */
export const SLDialogTerminBuchen = ({
  handleClose,
  open,
}: SLDialogTerminBuchenProps) => {
  const d = useMessages().dialogs;
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"), {
    noSsr: true,
  });

  // Consistent spacing values
  const spacing = {
    container: 20,
    section: 6,
    content: 4,
  } as const;

  // Responsive padding configuration
  const responsivePadding = {
    xs: 0.5,
    sm: 1,
    md: 2,
    lg: 4,
  } as const;

  return (
    <SLDialog
      title={d.appointment.title}
      open={open}
      onClose={handleClose}
      dialogProps={{
        maxWidth: "xl",
        fullScreen: isSmallScreen,
      }}
    >
      <Stack
        spacing={spacing.container}
        sx={{
          width: "100%",
          backgroundColor: theme.palette.secondary.light,
          borderRadius: theme.shape.borderRadius,
        }}
        direction={{ xs: "column-reverse", lg: "row" }}
      >
        {/* Left section - Information and team */}
        <Stack
          component="section"
          aria-label={d.appointment.heading}
          flex={0.45}
          spacing={spacing.section}
          p={responsivePadding}
        >
          <Typography variant="h2">{d.appointment.heading}</Typography>

          <Typography variant="body1">{d.appointment.description}</Typography>

          <SLTeamAvatarGroup />
          <Box>
            <SLTrustpilotRating />
          </Box>
        </Stack>

        {/* Right section - Calendar iframe */}
        <Stack
          component="section"
          aria-label={d.appointment.title}
          flex={0.55}
          sx={{
            borderRadius: theme.shape.borderRadius,
          }}
          p={{
            xs: responsivePadding.xs,
            sm: responsivePadding.sm,
            md: responsivePadding.md,
          }}
        >
          <Box
            sx={{
              backgroundColor: theme.palette.background.paper,
              borderRadius: theme.shape.borderRadius,
              maxHeight: "900px",
            }}
          >
            <SLBrevoCalendarIframe />
          </Box>
        </Stack>
      </Stack>
    </SLDialog>
  );
};
